import Link from 'next/link';
import { format } from 'date-fns';
import { ITikTokContentLog } from '@/server/models/tiktok-content-log';
import { CalendarIcon, ClockIcon, ArrowRightIcon } from 'lucide-react';
import { IInstagramContentLog } from '@/server/models/instagram-content-log';

// Determine how many ideas to show based on user's plan
const getMaxIdeasToShow = (plan: string | null) => {
  if (!plan || plan === 'starter') {
    return 3; // Free and starter plans see only 3 ideas
  }
  return Infinity; // Pro and studio plans see all ideas
};

export function IdeasTable({
  items,
  userPlan,
}: {
  items: (ITikTokContentLog | IInstagramContentLog)[];
  userPlan?: string | null;
}) {
  const maxIdeasToShow = getMaxIdeasToShow(userPlan || null);

  return (
    <div className="space-y-6">
      {items.map(tikTokContentLog => (
        <div
          key={tikTokContentLog.id}
          className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4"
        >
          {/* Header with metadata */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" />
                {format(tikTokContentLog.createdAt, 'MMM dd')}
              </div>
              <div className="flex items-center gap-1">
                <ClockIcon className="w-3 h-3" />
                {format(tikTokContentLog.createdAt, 'h:mm a')}
              </div>
            </div>
            <Link
              className="group/btn flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-md hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25 text-xs font-medium"
              href={`/dashboard/ideas/${tikTokContentLog.id}`}
            >
              <span>Details</span>
              <ArrowRightIcon className="w-3 h-3 group-hover/btn:translate-x-0.5 transition-transform duration-200" />
            </Link>
          </div>

          {/* Content area */}
          <div>
            {tikTokContentLog.error ? (
              <div className="text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm">
                <span className="font-medium">Error:</span> {tikTokContentLog.error.message}
              </div>
            ) : tikTokContentLog.data.ideas.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {[...tikTokContentLog.data.ideas]
                  .sort((a, b) => {
                    const order = { winner: 0, experiment: 1, crazy: 2 };
                    return (order[a.type] ?? 99) - (order[b.type] ?? 99);
                  })
                  .slice(0, maxIdeasToShow)
                  .map((idea, i) => (
                    <Link
                      key={i}
                      href={`/dashboard/ideas/${tikTokContentLog.id}?idea=${i + 1}`}
                      className="group/idea flex items-start gap-2 p-2 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                    >
                      {idea.type === 'crazy' && (
                        <div className="flex-shrink-0 w-1.5 h-1.5 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full mt-1.5"></div>
                      )}
                      {idea.type === 'winner' && (
                        <div className="flex-shrink-0 w-1.5 h-1.5 bg-gradient-to-r from-green-400 to-emerald-600 rounded-full mt-1.5"></div>
                      )}
                      {idea.type === 'experiment' && (
                        <div className="flex-shrink-0 w-1.5 h-1.5 bg-gradient-to-r from-blue-400 to-cyan-600 rounded-full mt-1.5"></div>
                      )}
                      {!(
                        idea.type === 'crazy' ||
                        idea.type === 'winner' ||
                        idea.type === 'experiment'
                      ) && (
                        <div className="flex-shrink-0 w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mt-1.5"></div>
                      )}
                      <p
                        className="text-white/90 text-sm leading-relaxed group-hover/idea:text-white transition-colors duration-200 overflow-hidden"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {idea.verbalHook}
                      </p>
                    </Link>
                  ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-500"></div>
                <span>Analyzing your content...</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
